import React, { useEffect, useState } from 'react';
import { List } from '@material-ui/core';

import { useSideNavigationMenuItemStyles } from './SideNavigationMenuItems.styles';
import { SideNavigationMenuItem } from './SideNavigationMenuItem/SideNavigationMenuItem';
import { getCurrentPath, isDefaultRoute, isEmptyList, isEmptyPath } from './SideNavigationMenuItemsHelper';
import { ISideNavigationMenuItemsConfig } from '../SideNavigationMenuConfiguration';

export interface ISideNavigationMenuItemsProps {
    hash?: string;
    items: ISideNavigationMenuItemsConfig[];
    isSendboard: boolean;
    handleOpenSendboardLeaveModal: (route: string) => void;
}

export const SideNavigationMenuItems = ({
    hash,
    items,
    isSendboard,
    handleOpenSendboardLeaveModal,
}: ISideNavigationMenuItemsProps) => {
    const [selectedMenu, setSelectedMenu] = useState<ISideNavigationMenuItemsConfig | null>(null);
    const [selectedSubMenu, setSelectedSubMenu] = useState<ISideNavigationMenuItemsConfig | null>(null);
    const [expandedMenu, setExpandedMenu] = useState<ISideNavigationMenuItemsConfig | null>(null);
    const [isRouteAvailable, setIsRouteAvailable] = useState<boolean>(true);
    const [menuConfig, setMenuConfig] = useState<ISideNavigationMenuItemsConfig[]>(items);

    const classes = useSideNavigationMenuItemStyles();

    useEffect(() => {
        const path = getCurrentPath();

        if (!isEmptyPath(path)) {
            setMenuConfig(itemsMapperByPath(items, path));
        }
    }, []);

    useEffect(() => {
        const isDifferentConfig = items !== menuConfig;

        if (isDifferentConfig) {
            const path = getCurrentPath();
            setMenuConfig(itemsMapperByPath(items, path));
        }
    }, [items]);

    useEffect(() => {
        if (hash) {
            setMenuConfig(itemsMapperByPath(items, hash));
        }
    }, [hash]);

    useEffect(() => setIsRouteAvailable(!isSendboard), [isSendboard]);

    const itemsMapperByPath = (menuItems: ISideNavigationMenuItemsConfig[], path: string) => {
        return menuItems.map((item) => {
            item.default = item.link === path || item.fallbackLink === path;

            if (item.default) {
                setSelectedMenu(item);
            }

            if (!isEmptyList(item.subItems)) {
                item.subItems.forEach((subItem) => {
                    subItem.default =
                        subItem.link === path ||
                        subItem.fallbackLink === path ||
                        path.includes(subItem.link) ||
                        (subItem.fallbackLink && path.includes(subItem.fallbackLink)) ||
                        path.includes(subItem.baseLink);

                    if (subItem.default) {
                        item.default = true;
                        setExpandedMenu(item);
                        setSelectedMenu(item);
                        setSelectedSubMenu(subItem);
                    }
                });
            }

            return item;
        });
    };

    const onSubMenuItemClick = (item: ISideNavigationMenuItemsConfig) => {
        if (selectedSubMenu !== item) {
            setSelectedSubMenu(item);
        }
    };

    const onMenuItemClick = (item: ISideNavigationMenuItemsConfig) => {
        const hasSubItems = item.subItems && item.subItems.length > 0;
        const shouldOpenSubMenu = hasSubItems && expandedMenu !== item;

        if (selectedMenu !== item) {
            setSelectedMenu(item);
        }

        if (shouldOpenSubMenu) {
            setExpandedMenu(item);
        } else if (!hasSubItems) {
            setExpandedMenu(null);
        }
    };

    const menuItemRouteHandler = (item: ISideNavigationMenuItemsConfig): void => {
        const goRouteHandler = (): string => {
            const hasSubItems = item.subItems.length > 0;
            const linkWithoutHash = hasSubItems ? item.subItems[0].baseLink : item.link;
            onMenuItemClick(item);
            return linkWithoutHash.substring(1);
        };

        if (isSendboard) {
            const route = goRouteHandler();
            handleOpenSendboardLeaveModal(route);
        } else {
            onMenuItemClick(item);
        }
    };

    const renderItem = (item: ISideNavigationMenuItemsConfig) => {
        return (
            item.visible && (
                <SideNavigationMenuItem
                    expandedMenu={expandedMenu}
                    selectedSubMenu={selectedSubMenu}
                    item={item}
                    onClick={menuItemRouteHandler}
                    onSubMenuItemClick={onSubMenuItemClick}
                    isSelected={item === selectedMenu}
                    isSendboard={isSendboard}
                />
            )
        );
    };

    return (
        <div className={classes.items}>
            <List component="nav">
                {menuConfig.map((item: ISideNavigationMenuItemsConfig) => {
                    const keyModifier = `${item.link}_${item.default}`;

                    if (isDefaultRoute(item, selectedMenu)) {
                        menuItemRouteHandler(item);
                    }

                    return isEmptyList(item.subItems) ? (
                        <a className={classes.link} href={isRouteAvailable ? item.link : undefined} key={keyModifier}>
                            {renderItem(item)}
                        </a>
                    ) : (
                        <div key={keyModifier}>{renderItem(item)}</div>
                    );
                })}
            </List>
        </div>
    );
};
